/**
 * Static dimension metadata for the main thread.
 *
 * Contains only labels, keys, and pure functions — no data dependencies.
 * Heavy computation (filtering, sparklines, fuzzy join) happens in the Web Worker.
 */

// ── Dimension registry (metadata + render facets) ──────────────
//
// Each dim entry is the single source of truth for that dim's
// metadata (key/label/sort) and any optional render facets:
//   - sentenceLabel              preserves acronyms/proper names in prose
//   - renderIcon(id, meta)      → { viewBox, paths } SVG spec, or null
//   - renderSecondary(id, meta) → string secondary line, or null
//   - renderTone(id, meta)      → CSS color string, or null
// Facets are optional. A dim with no facets renders as plain text via
// scopeStepLabel(step), unchanged from before. The cache loader
// (fetchLabelsViaAnalytics, below) populates a per-(dim,id) `meta`
// object that the facets read from — agents get { agentKey } from
// deriveAgentIdentity, endpoints get { hostname, registeredUser }
// from the extended ENTITY_TYPE_ENDPOINT query, etc.

import { ANALYTICS_SUPPORTED_DIM_PLURALS } from "./featureFlags.js";
import { scopeSegments } from "./routeModel.js";
import { deriveAgentIdentity } from "./agentIdentity.js";
import { LOGO_DEFS, PLACEHOLDER_LOGO, AGENT_COLORS } from "./dimensionRenderers.js";
import { SORT_FEWEST } from "./dimSort.js";

// Fewest-first twins of the "activity" sort. Each sits right after its
// most-first option so the menu reads most, fewest, then the rest.
const FEWEST_TRACES = [SORT_FEWEST, "Fewest traces"];
const LEAST_ACTIVE = [SORT_FEWEST, "Least active"];

// Full registry, in display order. The exported `DIMENSIONS` is the
// *visible* subset — every consumer that iterates DIMENSION_LIST or looks
// up metadata by key naturally sees it without per-call flag checks. Two
// The analytics catalogue determines the visible subset.
// Direct lookups for hidden dims (`DIMENSIONS["files"]`) return undefined;
// back-compat URL-parsing code can import `_ALL_DIMENSIONS`
// if it needs to recognise a hidden dim's key.
const _ALL_DIMENSIONS = {
  agents: {
    key: "agents", label: "Agents", sortDefault: "activity",
    sorts: [["activity", "Most active"], LEAST_ACTIVE, ["tokens", "Most tokens"], ["name", "A-Z"]],
    renderIcon: (_id, meta) => (meta?.agentKey && LOGO_DEFS[meta.agentKey]) || PLACEHOLDER_LOGO,
    renderTone: (_id, meta) => (meta?.agentKey && AGENT_COLORS[meta.agentKey]) || null,
  },
  endpoints: {
    key: "endpoints", label: "Endpoints", sortDefault: "activity",
    sorts: [["activity", "Most active"], LEAST_ACTIVE, ["tokens", "Most tokens"], ["name", "A-Z"]],
    renderSecondary: (_id, meta) => meta?.registeredUser || null,
    // Hostnames + registered_user change slowly — persist a week, refresh
    // in the background after a day so reloads render instantly.
    persist: { softMs: 24 * 3600_000, hardMs: 7 * 86400_000 },
  },
  // First-class graph dimension (O-2400): keyed on the directory user
  // (source_user_id) assigned to the endpoint a prompt ran on, via the
  // endpoint_user_assignments bridge. Scopes / breaks down / contrasts
  // server-side like any other dim (replaces the old derived-rewrite adapter).
  // Label (display name) + meta (department/email) load from the assignment
  // row via fetchLabelsViaAnalytics; assignments change slowly — persist like
  // endpoints.
  identities: {
    key: "identities", label: "Identities", sortDefault: "activity",
    sorts: [["activity", "Most active"], LEAST_ACTIVE, ["name", "A-Z"]],
    renderSecondary: (_id, meta) => meta?.department || meta?.email || null,
    persist: { softMs: 24 * 3600_000, hardMs: 7 * 86400_000 },
  },
  models: { key: "models", label: "Models", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["tokens", "Most tokens"], ["name", "A-Z"]] },
  clusters: {
    key: "clusters", label: "Topics", sortDefault: "activity", hierarchical: true,
    sorts: [["activity", "Most traces"], FEWEST_TRACES, ["tokens", "Most tokens"], ["name", "A-Z"]],
    // Deliberately NOT persisted to IDB: cluster_id → name is
    // generation-specific (the same id names a different cluster across
    // generations), so a persisted label survives into a later
    // generation and renders a stale/empty name on reload. Labels are
    // served from the freshly-fetched cluster tree instead (see
    // fetchLabelsViaAnalytics), which is always the current generation.
  },
  projects: { key: "projects", label: "Projects", sortDefault: "activity", hierarchical: true, sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  sessions: { key: "sessions", label: "Traces", sortDefault: "activity", sorts: [["activity", "Most active"], LEAST_ACTIVE, ["tokens", "Most tokens"], ["recent", "Recent"], ["name", "A-Z"]] },
  tools: { key: "tools", label: "Tools", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  mcp_servers: { key: "mcp_servers", label: "MCP servers", sentenceLabel: "MCP servers", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  files: { key: "files", label: "Files", sortDefault: "activity", hierarchical: true, sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  commands: { key: "commands", label: "Commands", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  hosts: { key: "hosts", label: "Hosts", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  urls: { key: "urls", label: "URLs", sentenceLabel: "URLs", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  skills: { key: "skills", label: "Skills", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  // Signal-backed adapter dim — per-user memories a harness tracks, read from the
  // backend `memory` signal_tags (flat, keyed on slug; the index is folded out).
  // The full content + version drift render in the per-memory Detail lens. See
  // memories.js + memoryAdapter. Flat, so NOT hierarchical (was project→file).
  memories: { key: "memories", label: "Memories", sortDefault: "activity", sorts: [["activity", "Most traces"], FEWEST_TRACES, ["name", "A-Z"]] },
  // Synthetic dim — entries are saved "signals" (triggers) evaluated
  // client-side over hydrated prompts (see signalsDim.js). The intended
  // interaction is the SIGNALS dimension lens (focusDim → alert console),
  // not value-level scope filtering, so signals is deliberately absent from
  // DASHBOARD_DIM_TO_API — a signal step can't server-filter.
  signals: { key: "signals", label: "Signals", sortDefault: "activity", sorts: [["activity", "Most fired"], ["name", "A-Z"]] },
};

export { _ALL_DIMENSIONS };

export function isDimensionVisible(key) {
  return ANALYTICS_SUPPORTED_DIM_PLURALS.has(key);
}

export const DIMENSIONS = Object.fromEntries(
  Object.entries(_ALL_DIMENSIONS).filter(([k]) => isDimensionVisible(k)),
);

export const DIMENSION_KEYS = Object.keys(DIMENSIONS);
export const DIMENSION_LIST = DIMENSION_KEYS.map((k) => DIMENSIONS[k]);

export function dimensionSentenceLabel(dim, fallback = "") {
  const label = dim?.label || fallback || "";
  return dim?.sentenceLabel || String(label).toLowerCase();
}

// ── Fuzzy join dimension metadata ───────────────────────────────

export const FUZZY_DIMENSIONS = [
  { id: "session", label: "Same trace", weight: 50, description: "In the same trace, nearby in time" },
  { id: "endpoint", label: "Same endpoint", weight: 20, description: "Same endpoint, nearby time, different trace" },
  { id: "files", label: "Shared files", weight: 10, description: "Traces touching the same file paths" },
  { id: "hosts", label: "Shared hosts", weight: 8, description: "Traces hitting the same network hosts" },
  { id: "skills", label: "Shared skills", weight: 5, description: "Traces using the same skills" },
  { id: "cluster", label: "Same topic", weight: 3, description: "Traces in the same topic" },
  { id: "agent", label: "Same agent", weight: 2, description: "Traces from the same model/agent" },
];

// ── Label resolution ────────────────────────────────────────────
//
// scopeStepLabel(step) is called all over the app — in tab strips,
// breadcrumbs, page titles, hover panels — and must be synchronous.
// Bulk-preloading every (dim, id) at startup doesn't scale once the
// tenant has tens of thousands of sessions. So instead the cache is
// lazy: scopeStepLabel returns the cached name or the raw id, and
// schedules a batch fetch on cache misses; React components that want
// reactive re-rendering use the useLabel(step) hook.
//
// Calls within a ~50ms window are coalesced and grouped by dim, then
// resolved via the analytics API where possible (ENDPOINT for
// hostnames, ILO_CLUSTER for cluster names; agent/model is its own
// label so no fetch is needed). Unsupported dimensions remain unresolved.

import { useEffect, useState } from "react";
import { runQuery as runAnalyticsQuery, rowsToObjects } from "./analyticsClient.js";
import { readUrlParam } from "./urlParams.js";
import { getCurrentTenantIdSync } from "./userInfo.js";
import {
  labelCacheGet,
  labelCacheSet,
  labelCacheClear,
  preWarmLabelCache,
  onLabelCacheReady,
} from "./labelCache.js";
import { onGenerationPinChange, getGenerationRevision } from "./generationPin.js";
import { loadClusterTree } from "./clusterTree.js";
import { getSessionLabels } from "./sessionLabels.js";
import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";
import { DEFAULT_RANGE } from "./dateRangePresets.js";

// Generous time range for label lookups — entities are TTL-bound by
// the planner anyway, but we want any visible id to resolve regardless
// of how old the active scope is. Capped at the planner's 90-day
// max-window guard.
const LABEL_TIME_RANGE = { relative: { durationSeconds: String(90 * 86400) } };

const FLUSH_MS = 50;
const _cache = new Map();        // "dim:id" -> primary label string ("" for not-found)
const _metaCache = new Map();    // "dim:id" -> meta object (only for dims that supply one)
const _pending = new Set();      // "dim:id" awaiting a flush
const _inflight = new Set();     // "dim:id" currently being fetched
const _subscribers = new Set();  // callbacks fired after a cache update
let _flushTimer = null;

function notify() {
  for (const cb of _subscribers) cb();
}

// These L1 keys intentionally stay compact (`dim:id`) because several render
// paths parse them. Clear the entire L1 synchronously at an auth boundary, and
// fence every asynchronous fill below with the auth epoch it started under.
// The persistent L2 is already tenant-keyed and is re-opened only after
// userInfo resolves the replacement tenant.
onAuthContextChange(() => {
  if (_flushTimer) clearTimeout(_flushTimer);
  _flushTimer = null;
  _cache.clear();
  _metaCache.clear();
  _pending.clear();
  _inflight.clear();
  notify();
});

// Drop every in-memory label-cache entry (L1 + inflight) whose key is under one
// of `dims` (a `${dim}:` prefix). Used to invalidate stale labels when a dim's
// label SOURCE changes out from under the cache — `requestLabel` never re-fetches
// a HIT, so without this a stale name sticks for the session. Callers handle any
// L2 (IDB) tier + `notify()` themselves, since those differ per source.
function purgeInMemoryDimLabels(dims) {
  const under = (k) => dims.some((d) => k.startsWith(`${d}:`));
  for (const k of [..._cache.keys()]) if (under(k)) _cache.delete(k);
  for (const k of [..._metaCache.keys()]) if (under(k)) _metaCache.delete(k);
  for (const k of [..._inflight]) if (under(k)) _inflight.delete(k);
}

// Bridge L2 (IDB) load completions into the existing notify path. Each
// time a persistent dim's slot finishes loading from IDB, any mounted
// components subscribed via useLabel/useLabels re-render and pick up
// the freshly-hydrated labels.
onLabelCacheReady(notify);

// Cluster labels are generation-scoped: the same integer cluster_id
// names a different cluster across generations, so a pin change makes
// every cached cluster label potentially wrong. A cache HIT never
// refetches, so without an explicit purge a stale name would stick for
// the session. On pin change, drop both cache layers for the cluster
// dim (L1 in-memory here, L2 IDB via labelCacheClear) and notify so
// mounted chips re-render and re-request. Other dims (endpoint, agent,
// session) are generation-independent and left alone.
onGenerationPinChange(() => {
  purgeInMemoryDimLabels(["cluster", "clusters"]);
  const tid = getCurrentTenantIdSync();
  if (tid) {
    // Plural is the dim key used on the cluster label path; clear the
    // singular too, defensively.
    labelCacheClear(tid, "clusters");
    labelCacheClear(tid, "cluster");
  }
  notify();
});

// Same hazard for signals, from the other direction. The signals catalog is
// REACTIVE — it re-hydrates to self-heal a rollout-skew listing (an old
// api-server serves signals with no analyst-facing `display_name`, so the first
// build labels a signal by its raw `name`; a later hydrate carries the title).
// Signal labels resolve through `signalAdapter.labelFor`, but this cache never
// re-requests a HIT (requestLabel short-circuits on `_cache.has`), so a `name`
// captured pre-heal would stick for the whole session — the sidebar chip shows
// `unicode_smuggling` while the detail panel (which reads the catalog directly)
// shows the healed title. Purge the signal dim's cached labels on a catalog
// content change so mounted chips re-resolve. Signals aren't L2-persisted (no
// `persist` on the dim), so only the in-memory layer needs clearing.
//
// Ready-gated: loading/error emits carry the prior labels, so purging on them
// would force a needless global re-render without moving any label.
function applySignalCatalogChange(state) {
  if (state?.status !== "ready") return;
  purgeInMemoryDimLabels(["signal", "signals"]);
  notify();
}

// Deferred dynamic import (not static) so `dimensionDefs` stays importable in
// non-DOM test envs that don't stub the API layer — a static `./signalCatalog.js`
// import pulls its `signalsApi → restClient` chain (which reads `API_BASE_URL` at
// eval) into every consumer of this core module.
import("./signalCatalog.js")
  .then(({ subscribeSignalCatalog }) => subscribeSignalCatalog(applySignalCatalogChange))
  .catch((err) => { console.warn("[signals] label-cache invalidation not installed:", err); });

// Test-only seams: seed / peek an in-memory label (the `${dim}:${id}` cache key)
// directly, so the catalog-driven purge above is observable — a signal's flushed
// label resolves to its own command id, indistinguishable from a cache miss, so
// there's no public read that would reveal the purge.
export function _seedDimLabelForTests(dim, id, label, meta = null) {
  _cache.set(`${dim}:${id}`, label);
  if (meta) _metaCache.set(`${dim}:${id}`, meta);
}
export function _peekDimLabelForTests(dim, id) { return _cache.get(`${dim}:${id}`); }

// Persistent dim keys, snapshot once — the registry doesn't mutate.
const PERSISTENT_DIM_KEYS = DIMENSION_LIST.filter((d) => d.persist).map((d) => d.key);

// Exported for direct test coverage of the per-dim label mapping — notably the
// empty-model → "(no model)" branch (O-1889).
export async function fetchLabelsViaAnalytics(dim, ids) {
  // Loader return shape: `{ id: string }` (legacy — label only) or
  // `{ id: { label, meta } }` (label + per-id meta consumed by the
  // dim's render facets). flush() handles both.

  // agents: the provider tag (e.g. "anthropic", "openai") IS the label,
  // but we run it through deriveAgentIdentity to (a) humanize via the
  // canonical PROVIDER_TAG_TO_IDENTITY map ("anthropic" → "Claude") and
  // (b) stash the normalised key on meta so renderIcon / renderTone can
  // look up LOGO_DEFS / AGENT_COLORS.
  if (dim === "agents") {
    return Object.fromEntries(ids.map((id) => {
      const identity = deriveAgentIdentity(id);
      return [String(id), {
        label: identity.name,
        meta: { provider: id, agentKey: identity.key, agentName: identity.name },
      }];
    }));
  }
  // models: the model name string IS the label. An empty value is the
  // "unattributed" TOKEN bucket — map it to a readable "(no model)" so the
  // scope chip / column header / sidebar agree with the entries list (which labels
  // it the same way in executeModelEntries) rather than rendering a blank.
  if (dim === "models") {
    return Object.fromEntries(ids.map((id) => [String(id), String(id) || "(no model)"]));
  }
  // Other value-is-label dims: tools (tool call name from PROMPT_TOOL_CALL_NAMES),
  // files (resource path from PROMPT_RESOURCE_PATHS) — no round trip, no meta.
  if (dim === "tools" || dim === "files") {
    return Object.fromEntries(ids.map((id) => [String(id), String(id)]));
  }
  // identities (O-2400): the person's label (display name) + meta
  // (department/email) live on the DIRECTORY_USER entity — read them straight
  // off it, keyed by the stable directory source_id.
  if (dim === "identities") {
    const F = (f) => ({ stepAlias: "du", field: `DIMENSION_FIELD_DIRECTORY_USER_${f}` });
    const spec = {
      root: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
      timeRange: LABEL_TIME_RANGE,
      filter: { pred: { field: F("SOURCE_ID"), op: "OPERATOR_IN", value: ids.map(String) } },
      output: {
        dimensions: [
          { field: F("SOURCE_ID"),       outputAlias: "id" },
          { field: F("DISPLAY_NAME"),    outputAlias: "name" },
          { field: F("DEPARTMENT"),      outputAlias: "dept" },
          { field: F("EMAIL"),           outputAlias: "email" },
        ],
      },
    };
    const out = {};
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    for (const r of rows) {
      const id = String(r.id ?? "");
      if (!id || id in out) continue; // denormalized — first row per id wins
      const displayName = String(r.name || "");
      out[id] = {
        label: displayName || id,
        meta: { department: String(r.dept || ""), email: String(r.email || "") },
      };
    }
    return out;
  }
  // Adapter-backed dims (mcp_servers, memories, …): resolve
  // labels via the adapter. fetchLabels (async, may carry render meta —
  // e.g. an mcp_server's tool surface) takes priority; labelFor is the
  // sync fallback. See src/lib/dimAdapters.js.
  const { getDimAdapterByPlural } = await import("./dimAdapters.js");
  const adapter = getDimAdapterByPlural(dim);
  if (adapter) {
    if (typeof adapter.fetchLabels === "function") {
      try {
        return await adapter.fetchLabels(ids);
      } catch {
        // Fall through to the sync labelFor below.
      }
    }
    if (adapter.labelFor) {
      return Object.fromEntries(ids.map((id) => [String(id), String(adapter.labelFor(id) || id)]));
    }
  }
  // session: AI_SESSION has no human name field (the proto's
  // `latest_summary` standard metric was dropped per its reserved comment),
  // so a session would otherwise read as a meaningless id. Label it with the
  // summary of its MOST-RECENT prompt instead (see sessionLabels.js); fall
  // back to the short-UUID prefix the dashboard uses elsewhere (matches
  // sessionTitle in adaptTrace) for sessions with no usable summary.
  // getSessionLabels never throws.
  if (dim === "sessions") {
    // Scope the "most recent prompt" to the current timeline (?range=, default
    // 1M) — the same window the rest of the app reads — so a chip's label
    // matches its sidebar-entry label and the data on screen. shell's
    // toApiRange touches window at module scope, so import it (and
    // rangeToTimeRange) lazily behind a window guard: this keeps dimensionDefs
    // importable in a non-DOM context (tests). Browser-only path; tests don't
    // exercise it.
    let timeRange;
    if (typeof window !== "undefined") {
      const [{ toApiRange }, { rangeToTimeRange }] = await Promise.all([
        import("./shell.js"),
        import("./analyticsClient.js"),
      ]);
      timeRange = rangeToTimeRange(toApiRange(readUrlParam("range") || DEFAULT_RANGE));
    }
    const labels = await getSessionLabels(ids, timeRange);
    return Object.fromEntries(ids.map((id) => [
      String(id), labels.get(String(id)) || String(id).slice(0, 8),
    ]));
  }
  if (dim === "endpoints") {
    const spec = {
      root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
      timeRange: LABEL_TIME_RANGE,
      filter: { pred: {
        field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" },
        op: "OPERATOR_IN", value: ids.map(String),
      }},
      output: {
        dimensions: [
          { field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" },     outputAlias: "id" },
          { field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_HOSTNAME" },        outputAlias: "name" },
          { field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_REGISTERED_USER" }, outputAlias: "user" },
        ],
      },
    };
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    return Object.fromEntries(rows.map((r) => {
      const hostname = String(r.name || "");
      const registeredUser = String(r.user || "");
      return [String(r.id), {
        label: hostname,
        meta: { hostname, registeredUser },
      }];
    }));
  }
  if (dim === "clusters") {
    // Serve from the cluster tree — the same source the hierarchy
    // listing uses, fetched fresh after each generation invalidation,
    // so chips/headers and rows never disagree and there's no stale
    // cross-generation label. Any id the tree doesn't carry (tree load
    // failed, or an outlier) falls back to a direct ILO_CLUSTER lookup.
    const tree = await loadClusterTree();
    const out = {};
    const missing = [];
    for (const id of ids) {
      const node = tree.byId.get(String(id));
      if (node) out[String(id)] = node.name || "";
      else if (/^\d+$/.test(String(id))) missing.push(Number(id));
    }
    if (missing.length === 0) return out;
    const spec = {
      root: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
      timeRange: LABEL_TIME_RANGE,
      filter: { pred: {
        field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },
        op: "OPERATOR_IN", value: missing,
      }},
      output: {
        dimensions: [
          { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },   outputAlias: "id" },
          { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" }, outputAlias: "name" },
        ],
      },
    };
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    for (const r of rows) out[String(r.id)] = String(r.name || "");
    return out;
  }
  // Unsupported dim.
  return null;
}

async function flush() {
  const authContextEpoch = getAuthContextEpoch();
  const generationRevision = getGenerationRevision();
  _flushTimer = null;
  if (_pending.size === 0) return;
  const byDim = new Map();
  for (const key of _pending) {
    if (_inflight.has(key)) continue;
    const i = key.indexOf(":");
    const dim = key.slice(0, i);
    const id = key.slice(i + 1);
    if (!byDim.has(dim)) byDim.set(dim, []);
    byDim.get(dim).push(id);
    _inflight.add(key);
  }
  _pending.clear();
  if (byDim.size === 0) return;

  await Promise.all([...byDim.entries()].map(async ([dim, ids]) => {
    let labels = {};
    try {
      const fromAnalytics = await fetchLabelsViaAnalytics(dim, ids);
      if (fromAnalytics) {
        labels = fromAnalytics;
      }
    } catch {
      // Swallow — better to render ids than to throw inside a flush
      // that fires for every visible label on the page.
    }
    if (getAuthContextEpoch() !== authContextEpoch) return;
    if ((dim === "cluster" || dim === "clusters") && getGenerationRevision() !== generationRevision) return;
    const persistCfg = DIMENSIONS[dim]?.persist;
    const tenantId = persistCfg ? getCurrentTenantIdSync() : null;
    for (const id of ids) {
      const key = `${dim}:${id}`;
      const result = labels[id];
      if (result && typeof result === "object") {
        _cache.set(key, result.label ?? "");
        if (result.meta) _metaCache.set(key, result.meta);
      } else {
        _cache.set(key, result ?? "");
      }
      _inflight.delete(key);
      // Mirror successful resolutions to L2 so the next page load
      // renders the label synchronously instead of flashing the raw id.
      // Empty labels are skipped by labelCacheSet — those are stored
      // as "" in memory only so we don't re-fetch a known dead id.
      if (persistCfg && tenantId) {
        const label = _cache.get(key);
        if (label) labelCacheSet(tenantId, dim, id, label, _metaCache.get(key));
      }
    }
  }));
  notify();
}

// Batched, cache-aware label loader.
//
// `dim` is the PLURAL key the label cache is indexed by ("endpoints",
// "clusters", …). Returns a promise resolving to Map<idStr, label> with
// the resolved labels for every id. Already-cached ids resolve
// synchronously (via _cache hits or L2 IDB hydration in requestLabel);
// misses go through the existing flush pipeline and the promise waits
// on the notify channel until all requested ids have landed.
//
// Use this from non-React contexts (e.g. apiClient.js' contrast/axis
// label resolution) instead of issuing your own ENTITY_TYPE_ENDPOINT /
// ENTITY_TYPE_ILO_CLUSTER query — that bypasses the cache and causes
// the same id to get fetched twice when both the sidebar and the
// contrast/swimlane axes want the same hostname.
export function loadLabels(dim, ids) {
  const authContextEpoch = getAuthContextEpoch();
  const idStrs = (ids || []).map((id) => String(id));
  if (idStrs.length === 0) return Promise.resolve(new Map());
  for (const id of idStrs) requestLabel(dim, id);

  const snapshot = () => {
    const out = new Map();
    for (const id of idStrs) {
      const v = _cache.get(`${dim}:${id}`);
      out.set(id, v || id);
    }
    return out;
  };

  const allResolved = () => idStrs.every((id) => _cache.has(`${dim}:${id}`));
  if (allResolved()) return Promise.resolve(snapshot());

  return new Promise((resolve) => {
    const cb = () => {
      if (getAuthContextEpoch() !== authContextEpoch) {
        _subscribers.delete(cb);
        resolve(new Map(idStrs.map((id) => [id, id])));
        return;
      }
      if (!allResolved()) return;
      _subscribers.delete(cb);
      resolve(snapshot());
    };
    _subscribers.add(cb);
  });
}

export function requestLabel(dim, id) {
  if (!dim || id == null) return;
  const key = `${dim}:${id}`;
  if (_cache.has(key) || _inflight.has(key)) return;

  // L2 hydration: for dims that persist, check the IDB-backed cache
  // before scheduling a network fetch. A hit within the hard TTL
  // populates _cache synchronously so the caller's next _cache.get()
  // lands the real label without a render flash. If the entry is older
  // than the soft TTL we still queue a refresh so the label converges
  // to the current value in the background.
  //
  // Cold first paint: labelCacheGet returns null until the slot's
  // initial IDB load completes (~10-100ms). To shrink that window we
  // pre-warm *all* persistent dims as soon as we see a tenantId, not
  // just the one being requested — so by the time the second dim's
  // first label hits, its slot is usually already loaded.
  const persistCfg = DIMENSIONS[dim]?.persist;
  if (persistCfg) {
    const tenantId = getCurrentTenantIdSync();
    if (tenantId) {
      preWarmLabelCache(tenantId, PERSISTENT_DIM_KEYS);
      const entry = labelCacheGet(tenantId, dim, id, persistCfg.hardMs);
      if (entry) {
        _cache.set(key, entry.label);
        if (entry.meta) _metaCache.set(key, entry.meta);
        const softStale = persistCfg.softMs != null
          && Date.now() - entry.fetchedAt > persistCfg.softMs;
        if (!softStale) return;
        // Fall through to queue a background refresh.
      }
    }
  }

  _pending.add(key);
  if (!_flushTimer) _flushTimer = setTimeout(flush, FLUSH_MS);
}

// Canonical id list for a step. Steps may be multi-selected (ids: [...])
// for simple dims (endpoints / agents / models / sessions / clusters);
// legacy single-select shape (id: "...") is normalised to a 1-element list.
export function stepIds(step) {
  if (!step) return [];
  if (Array.isArray(step.ids) && step.ids.length) return step.ids;
  if (step.id != null) return [step.id];
  return [];
}

// "Primary" id — what code that hasn't been taught about multi-select still
// reads. Equal to ids[0] so the existing label-cache key + lookups still
// resolve sensibly.
export function stepPrimaryId(step) {
  return stepIds(step)[0];
}

// Whether a label is still being read (asked and not yet in the cache).
export function labelPending(dim, id) {
  const key = `${dim}:${id}`;
  return !_cache.has(key) && (_pending.has(key) || _inflight.has(key));
}

export function scopeStepLabel(step) {
  if (!step) return "Overview";
  const ids = stepIds(step);
  if (ids.length === 0) return "Overview";
  if (ids.length === 1) {
    const key = `${step.dim}:${ids[0]}`;
    // requestLabel runs first so L2 (localStorage) hydration can land
    // in _cache before we read it — otherwise the synchronous read
    // would miss a persisted label and we'd flash the raw id for one
    // render until the subscriber fired.
    if (!_cache.has(key)) requestLabel(step.dim, ids[0]);
    const cached = _cache.get(key);
    return cached || ids[0];
  }
  // Multi-select: comma-list up to 2 labels, then "+N more". Each label
  // resolves through the same cache path so the display tightens up as
  // names land.
  const visible = ids.slice(0, 2).map((id) => {
    const key = `${step.dim}:${id}`;
    if (!_cache.has(key)) requestLabel(step.dim, id);
    const cached = _cache.get(key);
    return cached || id;
  });
  const more = ids.length - visible.length;
  return more > 0 ? `${visible.join(", ")} +${more} more` : visible.join(", ");
}

// Synchronous read of the per-(dim, id) meta object populated by the
// cache loader. Returns null when no meta has been recorded — either
// because the dim doesn't supply one (sessions/clusters/models/etc.)
// or because the label hasn't been fetched yet. Triggers a label
// request on miss so the meta arrives on the next flush.
export function dimensionMeta(dim, id) {
  if (!dim || id == null) return null;
  const key = `${dim}:${id}`;
  if (!_cache.has(key)) requestLabel(dim, id);
  return _metaCache.get(key) || null;
}

// React hook: returns { label, meta } for a (dim, id) pair, subscribing
// to cache updates. Used by primitives (DimensionIcon, DimensionLabel)
// and by call sites that need the meta directly (e.g. an endpoint cell
// rendering a registered-user secondary line).
export function useDimensionEntry(dim, id) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!dim || id == null) return undefined;
    requestLabel(dim, id);
    const cb = () => force((n) => n + 1);
    _subscribers.add(cb);
    return () => { _subscribers.delete(cb); };
  }, [dim, id]);
  if (!dim || id == null) return { label: "", meta: null };
  const key = `${dim}:${id}`;
  // Sync call during render so L2 hydration populates _cache before
  // the read below — matches the pattern in scopeStepLabel. The
  // useEffect call above is now mainly the subscriber setup; requestLabel
  // is idempotent so the duplicate is a no-op.
  if (!_cache.has(key)) requestLabel(dim, id);
  const cached = _cache.get(key);
  return {
    label: cached || String(id),
    meta: _metaCache.get(key) || null,
  };
}

// React hook: returns scopeStepLabel(step) but subscribes to cache
// updates so the component re-renders when the label arrives.
export function useLabel(step) {
  const [, force] = useState(0);
  const dim = step?.dim;
  const id = step?.id;
  useEffect(() => {
    if (!dim || id == null) return undefined;
    requestLabel(dim, id);
    const cb = () => force((n) => n + 1);
    _subscribers.add(cb);
    return () => { _subscribers.delete(cb); };
  }, [dim, id]);
  return scopeStepLabel(step);
}

// React hook: subscribe to cache updates without pinning any specific
// step. Components that call scopeStepLabel(step) for many steps in a
// render (e.g. a tab strip over scopePath) use this to force re-render
// when any awaited label arrives. Optionally pre-request a list of
// steps so the batch flush has them queued.
export function useLabels(steps) {
  const [tick, force] = useState(0);
  const sig = Array.isArray(steps)
    ? steps.map((s) => (s ? `${s.dim}:${stepIds(s).join("+")}` : "")).join("|")
    : "";
  useEffect(() => {
    if (Array.isArray(steps)) {
      for (const s of steps) {
        if (!s) continue;
        for (const id of stepIds(s)) requestLabel(s.dim, id);
      }
    }
    const cb = () => force((n) => n + 1);
    _subscribers.add(cb);
    return () => { _subscribers.delete(cb); };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  // Return the tick so consumers can include it in useMemo deps.
  // Required when a memo derives values from scopeStepLabel(...): the
  // first call returns the id-as-label fallback and schedules a fetch,
  // so the memo would stay stuck on that fallback unless it knows to
  // recompute when labels arrive.
  return tick;
}

// Compatibility shim — older code paths used setLabelMap to seed the
// label cache from an initialization payload. The lazy cache makes that
// unnecessary; ignore the call so existing callers don't crash.
export function setLabelMap(_labels) {
  // no-op
}

// ── Scope path encoding / decoding ──────────────────────────────

// Path encodes one segment per step: `dim:id1,id2,id3`. We use `,`
// (not `+`) as the join token because URLSearchParams parses values
// per application/x-www-form-urlencoded, which decodes `+` → space.
// Each id is encodeURIComponent'd first, and encodeURIComponent maps
// "," → "%2C" — so a literal `,` in the wire form is always a separator,
// never part of an id.
export function encodeScopePath(scopePath) {
  if (!scopePath.length) return "";
  return scopePath
    .map((s) => {
      const ids = stepIds(s);
      const enc = ids.map(encodeURIComponent).join(",");
      return `${s.dim}:${enc}`;
    })
    .join("/");
}

export function decodeScopePath(str) {
  return scopeSegments(str)
    .map(({ dim, rest }) => {
      // An empty rest is a legitimately empty-string id value — e.g. the
      // "(no model)" bucket where TOKEN_MODEL is "" — NOT "no ids".
      // Preserve it as [""] so that row stays selectable. The old blanket
      // `.filter(s => s.length)` dropped it, which dropped the whole step on
      // the encode→decode round-trip the scope makes through the tab/URL
      // (tabModel.js) — so clicking that row never registered a scope change.
      // Accept legacy `+` separator too in case any persisted URLs from the
      // brief `+`-using window are still around.
      const ids = rest === ""
        ? [""]
        : rest.split(/[,+]/).map(decodeURIComponent).filter((s) => s.length);
      if (ids.length === 0) return null;
      // Keep step.id populated for any code path that hasn't been taught
      // about ids[]. New code reads stepIds() / stepPrimaryId().
      return { dim, ids, id: ids[0] };
    })
    .filter((s) => s && DIMENSIONS[s.dim]);
}

// Dims whose ids compose cleanly under a single SQL `IN(...)` predicate
// — flat, single-column lookup. Hierarchical/artifact dims (project,
// file, host, skill) carry path / kind side-data per id, so multi-select
// would require multiple semi-joins per step. v1 restricts those to
// single-select; cmd-click is a no-op there.
export const MULTI_SELECTABLE_DIMS = new Set([
  "agents", "models", "endpoints", "sessions", "clusters",
  // Identities rewrite to a union of endpoint ids, so multi-select
  // composes cleanly (union of each identity's endpoint set).
  "identities",
]);

export function canMultiSelect(dimKey) {
  return MULTI_SELECTABLE_DIMS.has(dimKey);
}

// ── Runtime, per-tenant dim availability ────────────────────────
// A dim can require an async, per-tenant data fact beyond the static visibility
// gates (isDimensionVisible). Today only `memories`: its backend signal is
// per-tenant (deploy_to: none) with no resource_paths fallback, so it's shown
// only for a tenant that actually has memory data — otherwise it renders empty.
// The probe result is resolved by the caller (ScopeSidebar's
// getMemorySignalAvailable query) and passed IN, so this stays a pure predicate:
// undefined (loading) / false (absent) → gated out; true → shown.
export function passesTenantAvailability(dimKey, memoriesAvailable) {
  if (dimKey !== "memories") return true;
  return memoriesAvailable === true;
}

// ── Entry sorting (pure, no data dependency) ────────────────────

export function sortEntries(entries, mode = "activity") {
  return [...entries].sort((a, b) => {
    if (mode === "name") return (a.label || "").localeCompare(b.label || "");
    if (mode === "recent") return (b.recency || 0) - (a.recency || 0);
    return (b.activity || 0) - (a.activity || 0);
  });
}

// ── Timeline merge (pure, no data dependency) ───────────────────

export function mergeTimeline(anchors, contextTraces, sort = "newest") {
  const merged = [
    ...anchors.map((t) => ({ trace: t, score: 0, matches: [], isContext: false })),
    ...contextTraces,
  ];
  merged.sort((a, b) => {
    if (sort === "oldest") return a.trace.timestamp - b.trace.timestamp;
    return b.trace.timestamp - a.trace.timestamp;
  });
  return merged;
}
